import type { ReactNode } from "react";

import { cn } from "../lib/cn";

export interface PageShellProps {
  children: ReactNode;
  className?: string;
  /** Two-column on phone landscape and desktop (home forecast). */
  split?: boolean;
}

const shellBase = [
  "mx-auto flex w-full flex-1 flex-col py-page-y px-page",
  "sm:max-w-xl sm:mx-auto",
];

const shellSplit = [
  "phone-landscape:max-lg:grid phone-landscape:max-lg:grid-cols-[2fr_3fr] phone-landscape:max-lg:items-start phone-landscape:max-lg:gap-4",
  "lg:max-w-5xl lg:grid lg:grid-cols-[minmax(280px,360px)_1fr] lg:items-start lg:gap-6",
];

export function PageShell({ children, className, split = false }: PageShellProps) {
  return (
    <div className={cn(...shellBase, ...(split ? shellSplit : []), className)}>{children}</div>
  );
}

export interface PageShellColumnProps {
  children: ReactNode;
  className?: string;
  /** Span all grid columns (banners, alerts). */
  fullWidth?: boolean;
  /** Pin column content while the sibling column scrolls (split layouts). */
  sticky?: boolean;
  /** Viewport header to offset sticky top (default AppBar on home). */
  stickyBelow?: "app-bar" | "detail-header";
}

const stickyByHeader = {
  "app-bar": [
    "phone-landscape:max-lg:sticky phone-landscape:max-lg:top-below-app-bar phone-landscape:max-lg:self-start",
    "lg:sticky lg:top-below-app-bar lg:self-start",
  ],
  "detail-header": [
    "phone-landscape:max-lg:sticky phone-landscape:max-lg:top-below-detail-header phone-landscape:max-lg:self-start",
    "lg:sticky lg:top-below-detail-header lg:self-start",
  ],
} as const;

export function PageShellColumn({
  children,
  className,
  fullWidth = false,
  sticky = false,
  stickyBelow = "app-bar",
}: PageShellColumnProps) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col",
        fullWidth && "phone-landscape:max-lg:col-span-2 lg:col-span-2",
        ...(sticky ? stickyByHeader[stickyBelow] : []),
        className,
      )}
    >
      {children}
    </div>
  );
}
